import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Users, 
  BookOpen, 
  Target, 
  PenTool, 
  Heart,
  CheckCircle,
  FileText,
  Lightbulb,
  MessageSquare,
  Sparkles
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PropositionGuideProps {
  propositionId: string;
  onClose: () => void;
}

export const PropositionGuide: React.FC<PropositionGuideProps> = ({ propositionId, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [reflections, setReflections] = useState<{[key: string]: string}>({});
  const [aiFeedback, setAiFeedback] = useState<{[key: string]: string}>({});
  const [isGeneratingFeedback, setIsGeneratingFeedback] = useState(false);

  const propositionData = {
    '1': {
      title: 'Teachers are Committed to Students and Their Learning',
      icon: <Heart className="h-8 w-8" />,
      color: 'bg-blue-500',
      description: 'Accomplished teachers base their practice on the fundamental belief that all students can learn and meet high expectations.',
      keyPoints: [
        'Recognize individual differences and adjust practice accordingly',
        'Understand how students develop and learn',
        'Treat students equitably and with respect',
        'Mission extends beyond developing cognitive capacity'
      ],
      evidence: [
        'Differentiated instruction examples',
        'Student relationship building strategies',
        'Accommodation and modification documentation',
        'Cultural responsiveness practices'
      ],
      reflectionPrompts: [
        'How do you demonstrate your belief that all students can learn?',
        'What specific strategies do you use to build relationships with students?',
        'How do you accommodate different learning styles and needs?',
        'What evidence shows your commitment to student equity?'
      ]
    },
    '2': {
      title: 'Teachers Know the Subjects They Teach and How to Teach Those Subjects to Students',
      icon: <BookOpen className="h-8 w-8" />,
      color: 'bg-green-500',
      description: 'Teachers have a rich understanding of the subjects they teach and appreciate how knowledge in their subject is created, organized, linked to other disciplines, and applied.',
      keyPoints: [
        'Deep subject matter knowledge',
        'Understanding of pedagogical content knowledge',
        'Knowledge of curriculum and standards',
        'Ability to make content accessible to all learners'
      ],
      evidence: [
        'Curriculum alignment documentation',
        'Cross-curricular connections',
        'Age-appropriate content adaptation',
        'Assessment of student understanding'
      ],
      reflectionPrompts: [
        'How do you demonstrate deep content knowledge in your teaching?',
        'What strategies do you use to make complex concepts accessible?',
        'How do you connect your subject to other disciplines?',
        'What evidence shows your pedagogical content knowledge?'
      ]
    },
    '3': {
      title: 'Teachers are Responsible for Managing and Monitoring Student Learning',
      icon: <Target className="h-8 w-8" />,
      color: 'bg-purple-500',
      description: 'Teachers create, enrich, maintain, and alter instructional settings to capture and sustain student interest and to make the most effective use of time.',
      keyPoints: [
        'Effective classroom management',
        'Strategic use of instructional time',
        'Monitoring student progress',
        'Adjusting instruction based on student needs'
      ],
      evidence: [
        'Classroom management strategies',
        'Time management documentation',
        'Progress monitoring systems',
        'Instructional adjustment examples'
      ],
      reflectionPrompts: [
        'How do you create an effective learning environment?',
        'What systems do you use to monitor student progress?',
        'How do you adjust instruction based on student data?',
        'What evidence shows your classroom management effectiveness?'
      ]
    },
    '4': {
      title: 'Teachers Think Systematically About Their Practice and Learn from Experience',
      icon: <PenTool className="h-8 w-8" />,
      color: 'bg-orange-500',
      description: 'Teachers are models of educated persons, exemplifying intellectual curiosity and a commitment to learning that extends beyond their professional lives.',
      keyPoints: [
        'Reflective practice habits',
        'Professional growth mindset',
        'Data-driven decision making',
        'Continuous improvement focus'
      ],
      evidence: [
        'Reflection documentation',
        'Professional development records',
        'Action research examples',
        'Growth goal setting'
      ],
      reflectionPrompts: [
        'How do you systematically reflect on your teaching practice?',
        'What data do you use to inform instructional decisions?',
        'How do you pursue professional growth?',
        'What evidence shows your commitment to continuous learning?'
      ]
    },
    '5': {
      title: 'Teachers are Members of Learning Communities',
      icon: <Users className="h-8 w-8" />,
      color: 'bg-teal-500',
      description: 'Teachers collaborate with other professionals on instructional policy, curriculum development, and staff development.',
      keyPoints: [
        'Professional collaboration',
        'Family and community engagement',
        'Contributing to school culture',
        'Leadership in educational improvement'
      ],
      evidence: [
        'Collaboration documentation',
        'Family engagement strategies',
        'Professional leadership examples',
        'Community partnership evidence'
      ],
      reflectionPrompts: [
        'How do you collaborate with colleagues to improve student learning?',
        'What strategies do you use to engage families?',
        'How do you contribute to your school\'s professional community?',
        'What leadership roles have you taken on?'
      ]
    }
  };

  const proposition = propositionData[propositionId as keyof typeof propositionData];

  if (!proposition) {
    return <div>Proposition not found</div>;
  }

  const handleReflectionChange = (key: string, value: string) => {
    setReflections(prev => ({ ...prev, [key]: value }));
  };

  const generateAIFeedback = async (key: string, type: 'reflection' | 'planning') => {
    const reflectionText = reflections[key];
    
    if (!reflectionText?.trim()) {
      toast.error('Please enter some text before requesting feedback');
      return;
    }

    setIsGeneratingFeedback(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-insights', {
        body: {
          content: reflectionText,
          context: `NBCT Core Proposition ${propositionId}: ${proposition.title} - ${type}`,
          type: 'proposition_feedback'
        }
      });

      if (error) throw error;
      
      const feedback = data.insights || 'Unable to generate feedback at this time.';
      setAiFeedback(prev => ({ ...prev, [`${key}-feedback`]: feedback }));
      toast.success('AI feedback generated!');
    } catch (error) {
      console.error('Error generating AI feedback:', error);
      toast.error('Failed to generate AI feedback');
    } finally {
      setIsGeneratingFeedback(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className={`p-3 rounded-lg ${proposition.color} text-white`}>
            {proposition.icon}
          </div>
          <div>
            <h2 className="text-2xl font-bold">Proposition {propositionId}</h2>
            <p className="text-xl font-medium">{proposition.title}</p>
            <p className="text-muted-foreground mt-2">{proposition.description}</p>
          </div>
        </div>
        <Button variant="outline" onClick={onClose}>
          Back to Overview
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="evidence">Evidence</TabsTrigger>
          <TabsTrigger value="reflection">Reflection</TabsTrigger>
          <TabsTrigger value="planning">Planning</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Key Points</CardTitle>
              <CardDescription>Core elements of this proposition</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {proposition.keyPoints.map((point, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 rounded-lg bg-muted/50">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>How This Connects to Components</CardTitle>
              <CardDescription>Where you'll demonstrate this proposition</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg border">
                  <h4 className="font-medium mb-2">Component 1: Content Knowledge</h4>
                  <p className="text-sm text-muted-foreground">
                    Demonstrate your understanding of how to apply this proposition in your content area
                  </p>
                </div>
                <div className="p-4 rounded-lg border">
                  <h4 className="font-medium mb-2">Component 2: Differentiation</h4>
                  <p className="text-sm text-muted-foreground">
                    Show how you adapt instruction based on this proposition
                  </p>
                </div>
                <div className="p-4 rounded-lg border">
                  <h4 className="font-medium mb-2">Component 3: Teaching Practice</h4>
                  <p className="text-sm text-muted-foreground">
                    Capture video evidence of this proposition in action
                  </p>
                </div>
                <div className="p-4 rounded-lg border">
                  <h4 className="font-medium mb-2">Component 4: Reflective Practice</h4>
                  <p className="text-sm text-muted-foreground">
                    Reflect on how this proposition guides your professional growth
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="evidence" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>Types of Evidence</span>
              </CardTitle>
              <CardDescription>Examples of evidence that demonstrate this proposition</CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible>
                {proposition.evidence.map((evidence, index) => (
                  <AccordionItem key={index} value={`evidence-${index}`}>
                    <AccordionTrigger>{evidence}</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Consider collecting specific examples and documentation that show:
                        </p>
                        <ul className="text-sm space-y-1 ml-4">
                          <li>• Before and after student work samples</li>
                          <li>• Planning documents and modifications</li>
                          <li>• Assessment data and analysis</li>
                          <li>• Communication records with stakeholders</li>
                          <li>• Professional learning documentation</li>
                        </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reflection" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <MessageSquare className="h-5 w-5" />
                <span>Reflection Prompts</span>
              </CardTitle>
              <CardDescription>Deepen your understanding through guided reflection</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {proposition.reflectionPrompts.map((prompt, index) => (
                  <div key={index} className="space-y-3">
                    <Label htmlFor={`reflection-${index}`} className="text-base font-medium">
                      {prompt}
                    </Label>
                    <Textarea
                      id={`reflection-${index}`}
                      placeholder="Write your reflection here..."
                      value={reflections[`prompt-${index}`] || ''}
                      onChange={(e) => handleReflectionChange(`prompt-${index}`, e.target.value)}
                      rows={4}
                    />
                    <Button
                      variant="outline"
                      onClick={() => generateAIFeedback(`prompt-${index}`, 'reflection')}
                      disabled={isGeneratingFeedback || !reflections[`prompt-${index}`]?.trim()}
                      className="w-full"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      {isGeneratingFeedback ? 'Getting AI Feedback...' : 'Get AI Feedback on Reflection'}
                    </Button>
                    
                    {aiFeedback[`prompt-${index}-feedback`] && (
                      <Card className="bg-green-50 border-green-200">
                        <CardHeader>
                          <CardTitle className="text-green-800 text-lg flex items-center gap-2">
                            <Sparkles className="h-5 w-5" />
                            AI Coaching Feedback
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-green-700 whitespace-pre-wrap text-sm">
                            {aiFeedback[`prompt-${index}-feedback`]}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="planning" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Lightbulb className="h-5 w-5" />
                <span>Action Planning</span>
              </CardTitle>
              <CardDescription>Plan how to strengthen this proposition in your practice</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="space-y-3">
                  <Label htmlFor="current-strengths" className="text-base font-medium">
                    Current Strengths
                  </Label>
                  <Textarea
                    id="current-strengths"
                    placeholder="What are you already doing well in this proposition?"
                    value={reflections['strengths'] || ''}
                    onChange={(e) => handleReflectionChange('strengths', e.target.value)}
                    rows={3}
                  />
                  <Button
                    variant="outline"
                    onClick={() => generateAIFeedback('strengths', 'planning')}
                    disabled={isGeneratingFeedback || !reflections['strengths']?.trim()}
                    className="w-full"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {isGeneratingFeedback ? 'Getting AI Feedback...' : 'Get AI Feedback'}
                  </Button>
                  
                  {aiFeedback['strengths-feedback'] && (
                    <Card className="bg-blue-50 border-blue-200">
                      <CardHeader>
                        <CardTitle className="text-blue-800 text-lg flex items-center gap-2">
                          <Sparkles className="h-5 w-5" />
                          AI Planning Feedback
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-blue-700 whitespace-pre-wrap text-sm">
                          {aiFeedback['strengths-feedback']}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                <div className="space-y-3">
                  <Label htmlFor="growth-areas" className="text-base font-medium">
                    Areas for Growth
                  </Label>
                  <Textarea
                    id="growth-areas"
                    placeholder="Where would you like to improve or develop further?"
                    value={reflections['growth'] || ''}
                    onChange={(e) => handleReflectionChange('growth', e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="space-y-3">
                  <Label htmlFor="action-steps" className="text-base font-medium">
                    Action Steps
                  </Label>
                  <Textarea
                    id="action-steps"
                    placeholder="What specific steps will you take to strengthen this proposition?"
                    value={reflections['actions'] || ''}
                    onChange={(e) => handleReflectionChange('actions', e.target.value)}
                    rows={4}
                  />
                </div>

                <div className="space-y-3">
                  <Label htmlFor="evidence-collection" className="text-base font-medium">
                    Evidence Collection Plan
                  </Label>
                  <Textarea
                    id="evidence-collection"
                    placeholder="How will you document and collect evidence of this proposition?"
                    value={reflections['evidence-plan'] || ''}
                    onChange={(e) => handleReflectionChange('evidence-plan', e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end space-x-2">
            <Button variant="outline">Save Draft</Button>
            <Button>Save Planning Notes</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};